/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useMediaQuery } from "@mui/material";
import GridLayout, { WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { GET_DASHBOARD, UPDATE_DASHBOARD } from "./dashboardStore";
import { WIDGET_REF_KEYS } from "../../../helpers/widgetRef";
import { sanitizeSettings } from "../../Homepage/customize/layoutStore";
import { makeCustomId, widgetById } from "../../Homepage/customize/widgetRegistry";
import LightSelect from "../../CompanyView/LightSelect";
import { UseIsAdmin } from "../../../hooks/auth";
import { UseSelectedWorkspace, UseCompanyWorkspaces, UseUsersDictionary } from "../../../hooks/company";
import WidgetCard from "./WidgetCard";
import ComponentCard from "./ComponentCard";
import CopyToDashboardModal from "./CopyToDashboardModal";
import ComponentsDrawer, { minSizeFor, sizeFor } from "./ComponentsDrawer";
import AddMetricModal from "./AddMetricModal";
import WidgetEditDrawer from "./WidgetEditDrawer";
import MiniMenu from "./MiniMenu";
import { defaultSize, defaultTypeForMetric, TEMPLATES, ACCENT_CYCLE } from "./widgetTypes";
import { PERIODS, periodLabelFor, resolveWidgetData } from "./useDashboardMetrics";
import { AVG_DAILY_REVENUE_ID, BEAT_YETI_RATE_ID, TECH_REVENUE_PER_HOUR_ID } from "../../../hooks/useDerivedRevenueMetrics";
import { IcBack, IcEdit, IcPlus, IcLock, IcGlobe, IcMove, IcExpand, IcClose, IcAlert, IcUsers, IcShield, IcTv, IcCheck, IcStat, IcBlank } from "./icons";
import { defaultNetBonus } from "../../../components/NetProfitBonus/netBonus";
import RangeCalendar from "../../Homepage/customize/RangeCalendar";
import "../../Homepage/customize/rangeCalendar.css";

const Grid = WidthProvider(GridLayout);

/* Mirrors the server's AUDIENCE_MODES. 'shared' is what new boards save;
   'workspaces' and 'players' are older spellings read the same way. */
const AUDIENCE_MODES = ["shared", "workspaces", "players"];

let _uid = 0;
const nid = () => `w${Date.now().toString(36)}${(_uid++).toString(36)}`;

/** Analytics grid: ensure hugging / tall registry cards aren't shorter than their floor. */
const fitComponentHeight = (w) => {
  if (w.kind !== "component" || !w.comp) return w;
  const def = widgetById(w.comp);
  if (!def) return w;
  const floor = minSizeFor(def).h;
  const h = Math.max(Number(w.h) || 0, floor);
  return h === w.h ? w : { ...w, h };
};

/**
 * Recover game components that were autosaved as empty metric cards
 * (kind/comp wiped, options left intact → every tile "Metric unavailable").
 */
const healWidget = (w) => {
  if (!w || typeof w !== "object") return w;
  if (w.kind === "component" && w.comp) return w;
  if (typeof w.metric === "string" && w.metric) {
    return { ...w, kind: "metric" };
  }
  const opts = w.options || {};
  let comp = typeof w.comp === "string" ? w.comp : "";
  if (!comp) {
    if (opts.across === true || /^leaderboard$/i.test(w.title || "")) {
      comp = "leaderboard";
    } else if (
      opts.look
      || opts.metricPlan
      || (Array.isArray(opts.metrics) && opts.metrics.length > 0)
    ) {
      const seed = String(w._id || w.i || "").replace(/[^a-z0-9]/gi, "");
      comp = seed ? `custom-${seed.slice(-16)}` : makeCustomId();
    }
  }
  if (!comp) return { ...w, kind: w.kind === "component" ? "component" : "metric" };
  return {
    ...w,
    kind: "component",
    comp,
    metric: "",
  };
};

// Give each server widget a stable client key for the grid.
const hydrate = (widgets) =>
  (widgets || []).map((w) => fitComponentHeight(healWidget({ ...w, i: w._id || w.i || nid() })));

/**
 * Below `lg`, react-grid-layout still lays out a fixed 12-column grid — on a
 * phone-width viewport that squeezes every widget into a sliver too narrow to
 * read. Since the canvas is already read-only on mobile (no drag/resize),
 * skip the absolute grid there and just stack widgets full-width in reading
 * order (top-to-bottom, left-to-right) so each chart renders at a legible size.
 */
function MobileWidgetStack({ widgets, metricsApi, boardCtx }) {
  const { loading } = metricsApi;
  const ordered = useMemo(
    () => [...widgets].sort((a, b) => (a.y || 0) - (b.y || 0) || (a.x || 0) - (b.x || 0)),
    [widgets]
  );
  return (
    <div className="db-mobile-stack">
      {ordered.map((w) => {
        const wd = w.kind === "component" ? null : resolveWidgetData(metricsApi, w);
        return (
          <div key={w.i} className="db-mobile-stack-item">
            {w.kind === "component" ? (
              <ComponentCard
                widget={w}
                period={metricsApi.period}
                editing={false}
                onDuplicate={() => {}}
                onRemove={() => {}}
                {...boardCtx}
              />
            ) : (
              <WidgetCard
                widget={w}
                metric={wd.metric}
                series={wd.series}
                summary={wd.summary}
                loading={loading}
                api={metricsApi}
                editing={false}
                onChangeType={() => {}}
                onEdit={() => {}}
                onDuplicate={() => {}}
                onRemove={() => {}}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
MobileWidgetStack.propTypes = {
  widgets: PropTypes.array.isRequired,
  metricsApi: PropTypes.object.isRequired,
  boardCtx: PropTypes.object,
};

/**
 * `readOnly` — the board as a shared viewer sees it: no saving of any kind,
 * and none of the editing chrome. It is how a player opens a board that was
 * shared with them (see pages/SharedDashboard), where the owner's toolbar
 * would be a row of buttons that 403 on use.
 */
export default function DashboardBuilder({ dashboard, metricsApi, onBack, onSaved, demo, readOnly, backLabel }) {
  // Everything that writes is off in both modes; `demo` is the visual-QA
  // harness, `readOnly` is a real board somebody else owns.
  const frozen = !!demo || !!readOnly;
  const isAdmin = UseIsAdmin();
  const selectedWorkspace = UseSelectedWorkspace();
  const workspaces = UseCompanyWorkspaces(true) || [];
  const usersDict = UseUsersDictionary();
  const wsNameById = useMemo(() => {
    const out = {};
    (workspaces || []).forEach((w) => { out[String(w._id)] = w.name; });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify((workspaces || []).map((w) => [String(w._id), w.name]))]);
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const { metrics, getSeries, getSummary, loading, period, setPeriod, customRange, setCustomRange } = metricsApi;
  const periodTitle = period === "custom" && customRange?.from && customRange?.to
    ? periodLabelFor("custom", customRange)
    : (PERIODS.find((p) => p.id === period) || PERIODS.find((p) => p.id === "month")).label;

  const [name, setName] = useState(dashboard.name || "Untitled dashboard");
  const [widgets, setWidgets] = useState(() => hydrate(dashboard.widgets));

  /* A manual gate is confirmed somewhere else entirely — from the notification,
     or from the card's own popover — and that write lands on the server without
     this board ever hearing about it. So the card kept saying "Not confirmed
     yet" beside a gate somebody had already ruled on. Re-read the board when
     that happens rather than leaving a stale answer on screen about money. */
  useEffect(() => {
    const onConfirmed = async (e) => {
      const boardId = e?.detail?.boardId;
      if (!boardId || String(boardId) !== String(dashboard._id)) return;
      try {
        const res = await GET_DASHBOARD(dashboard._id);
        const fresh = res?.data;
        if (fresh && Array.isArray(fresh.widgets)) setWidgets(hydrate(fresh.widgets));
      } catch (err) {
        /* Leave what is on screen; the next load will pick it up. */
      }
    };
    window.addEventListener("gmb:gate-confirmed", onConfirmed);
    return () => window.removeEventListener("gmb:gate-confirmed", onConfirmed);
  }, [dashboard._id]);

  /* Cross-workspace Viewing (Home parity): when any component scopes 2+
     workspaces, the toolbar offers All / per-workspace and cards inherit. */
  const [boardWs, setBoardWs] = useState("all");
  const boardWorkspaces = useMemo(() => {
    const ids = new Set();
    (widgets || []).forEach((w) => {
      if (w.kind !== "component") return;
      const ws = Array.isArray(w.options?.workspaceIds)
        ? w.options.workspaceIds.filter(Boolean).map(String)
        : [];
      if (ws.length >= 2) ws.forEach((id) => ids.add(id));
    });
    return [...ids];
  }, [widgets]);
  const hasCombined = boardWorkspaces.length >= 2;
  const hostId = selectedWorkspace?._id ? String(selectedWorkspace._id) : null;
  const inheritSources = useMemo(
    () => (hostId ? boardWorkspaces.filter((id) => id !== hostId) : boardWorkspaces),
    [boardWorkspaces, hostId],
  );
  const isRollupHost = hasCombined && !!hostId && !boardWorkspaces.includes(hostId);
  const shouldInherit = inheritSources.length >= 2 && isRollupHost;
  const inheritWorkspaceIds = shouldInherit ? inheritSources : [];
  const PERIOD_TO_WINDOW = {
    today: "today",
    yesterday: "yesterday",
    week: "week",
    lastWeek: "lastWeek",
    lastMonth: "lastMonth",
    month: "month",
    quarter: "quarter",
    ytd: "ytd",
    year: "ytd",
    custom: "custom",
  };
  const boardWindow = PERIOD_TO_WINDOW[period] || "month";
  const boardCustom = period === "custom" && (customRange?.from || customRange?.to)
    ? { ...customRange }
    : null;

  const wsOptions = useMemo(() => {
    const labels = {};
    (workspaces || []).forEach((w) => { labels[String(w._id)] = w.name; });
    return [
      { value: "all", label: "All workspaces" },
      ...boardWorkspaces.map((id) => ({ value: id, label: labels[id] || "Workspace" })),
    ];
  }, [workspaces, boardWorkspaces]);
  useEffect(() => {
    if (!hasCombined && boardWs !== "all") setBoardWs("all");
    else if (boardWs !== "all" && !boardWorkspaces.includes(boardWs)) setBoardWs("all");
  }, [hasCombined, boardWorkspaces, boardWs]);

  const [visibility, setVisibility] = useState(dashboard.visibility?.mode || "private");
  const [wsIds, setWsIds] = useState(() => (dashboard.visibility?.workspaceIds || []).map(String));
  // mode "players": the exact people invited by name, whichever workspace
  // they sit in. Kept separate from wsIds so switching between the two modes
  // doesn't clobber the other's picks while the editor is still open.
  const [playerIds, setPlayerIds] = useState(() => (dashboard.visibility?.userIds || []).map(String));
  const [showOnTv, setShowOnTv] = useState(!!dashboard.showOnTv);
  // Turning this on publishes the board to the workspace's TVs, so the enable
  // click is confirmed (and explained) first; turning it off is immediate.
  const [tvConfirm, setTvConfirm] = useState(false);
  /* Workspaces and named players are ONE audience, picked in one popover:
     a board often wants a whole team plus a couple of people from another,
     and making them separate modes meant choosing one and losing the other. */
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [audienceQuery, setAudienceQuery] = useState("");
  const audienceRef = useRef(null);
  useEffect(() => {
    if (!audienceOpen) return undefined;
    const onDown = (e) => {
      if (audienceRef.current && !audienceRef.current.contains(e.target)) setAudienceOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [audienceOpen]);
  /* A fresh board opens in edit mode so it isn't just an empty grid — but
     never for a viewer, who has no Edit button to turn it back off and
     whose changes would go nowhere. Edit mode is also what shows each
     card's own menu, so this keeps those out of a viewer's way. */
  const [editing, setEditing] = useState(!readOnly && (dashboard.widgets || []).length === 0);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  // The docked add drawer — same drawer as the workspace dashboard.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // A placed component being edited in the drawer (widget key), or null.
  const [compEditId, setCompEditId] = useState(null);
  // The docked drawer shrinks the board (see .db-root.panel-open in dashboards.css);
  // WidthProvider measures on window resize, so nudge it once the push transition
  // lands and again mid-way, so the grid reflows into the narrower width.
  useEffect(() => {
    const fire = () => window.dispatchEvent(new Event("resize"));
    const a = setTimeout(fire, 120);
    const b = setTimeout(fire, 260);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, [drawerOpen]);
  // The template this dashboard was built from — unfilled slots show as ghosts.
  const [templateId, setTemplateId] = useState(dashboard.template || "");
  // A ghost slot waiting to be filled by the next pick.
  const [pendingSlot, setPendingSlot] = useState(null);
  const [editId, setEditId] = useState(null);
  const [toast, setToast] = useState("");
  // Card the drag is hovering — gets the "will swap" highlight.
  const [swapTarget, setSwapTarget] = useState(null);
  const [presenting, setPresenting] = useState(false);

  const loadedRef = useRef(false);
  const toastTimer = useRef(null);
  const gridWrapRef = useRef(null);
  /* The sticky toolbar only grows its hairline once something has actually
     scrolled beneath it. A sentinel above the bar is cheaper and steadier than
     listening to scroll. */
  const aboveBarRef = useRef(null);
  const [barStuck, setBarStuck] = useState(false);
  useEffect(() => {
    const el = aboveBarRef.current;
    if (!el) return undefined;
    /* `position: sticky` is captured by the nearest ancestor that creates a
       scrollport, and Main's content Box sets `overflowX: auto` for wide pages
       like Scorekeeper. CSS cannot pair `overflow-x: auto` with
       `overflow-y: visible` — the Y axis computes to auto as well — so that Box
       became this bar's scroll container, and since it is the WINDOW that
       scrolls here, the bar never stuck to anything.

       Only this board needs the exception, so take it while the board is
       mounted and hand it straight back on the way out. The board is a grid and
       never needs the page to scroll sideways. */
    let relaxed = null;
    let prevOverflowX = "";
    for (let n = el.parentElement; n; n = n.parentElement) {
      const cs = window.getComputedStyle(n);
      if (cs.overflowY !== "visible") {
        if (n.scrollHeight <= n.clientHeight + 2) {
          relaxed = n;
          prevOverflowX = n.style.overflowX;
          n.style.overflowX = "visible";
        }
        break;
      }
    }
    if (typeof IntersectionObserver === "undefined") {
      return () => { if (relaxed) relaxed.style.overflowX = prevOverflowX; };
    }
    const sentinel = document.createElement("div");
    sentinel.setAttribute("aria-hidden", "true");
    sentinel.style.cssText = "position:absolute;height:1px;width:1px;pointer-events:none;";
    el.parentNode.insertBefore(sentinel, el);
    const io = new IntersectionObserver(
      ([e]) => setBarStuck(!e.isIntersecting),
      { threshold: 0 },
    );
    io.observe(sentinel);
    return () => {
      io.disconnect();
      if (sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
      if (relaxed) relaxed.style.overflowX = prevOverflowX;
    };
  }, []);
  // Pre-drag snapshot + dragged id: dropping a card onto another swaps the two
  // (each takes the other's slot) instead of keeping the pushed-around layout.
  const dragInfo = useRef(null);
  const skipNextLayout = useRef(false);
  const swapTargetRef = useRef(null);

  const flash = (msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 1800);
  };

  const [rangeOpen, setRangeOpen] = useState(false);

  // Restore saved board dateRange (including year + custom).
  useEffect(() => {
    const preset = dashboard.dateRange?.preset;
    const known = PERIODS.some((p) => p.id === preset);
    if (known) {
      setPeriod(preset);
      if (preset === "custom") {
        setCustomRange({
          from: dashboard.dateRange?.from || "",
          to: dashboard.dateRange?.to || "",
        });
      }
      return;
    }
    setPeriod("month");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dateRangePayload = () => {
    if (period === "custom") {
      return { preset: "custom", from: customRange?.from || "", to: customRange?.to || "" };
    }
    return { preset: period };
  };

  /* `list` defaults to the current `widgets` state (the normal debounced-save
     path below), but a caller with a widgets array it just computed itself —
     patchWidgetOptions, below — passes it explicitly so it can persist that
     exact snapshot without waiting for this render's `widgets` closure to
     catch up. */
  const serialize = useCallback(
    (list = widgets) =>
      list.map((raw) => {
        const w = healWidget(raw);
        const isComp = w.kind === "component" && !!w.comp;
        return {
          /* The server stores widgets as Mixed, so Mongoose never mints an _id
             for them - an id only survives a save if WE send it. Fall back to the
             client id so a card keeps one identity across reloads, which is what
             lets another card's saved reference (laborWidgetId) find it again. */
          _id: w._id || w.i,
          kind: isComp ? "component" : "metric",
          metric: isComp ? "" : (w.metric || ""),
          comp: isComp ? (w.comp || "") : "",
          title: w.title || "",
          chartType: w.chartType || "stat",
          x: w.x || 0,
          y: w.y || 0,
          w: w.w || 3,
          h: w.h || 3,
          options: isComp
            ? (sanitizeSettings(w.options || {}) || {})
            : (w.options || {}),
        };
      }),
    [widgets]
  );

  /* A card added this session lives under a throwaway client id (`nid()`)
     until its first save, when the server hands it a real _id. A card that
     reads a sibling (Revenue, Expenses & Labor → its Payroll vs Revenue card)
     stores that sibling's id in its options — so a pick made before the
     sibling's first save held an id that stopped existing, and the card
     answered "That payroll card is gone" after the next reload. Once the
     server's ids are known, stamp them onto the local widgets (so every later
     save sends them) and rewrite any reference still holding a throwaway one.
     The rewrite changes `widgets`, which autosaves the corrected references;
     with nothing to adopt it returns the same array, so it can't loop. */
  const adoptServerIds = (sent, saved) => {
    if (!Array.isArray(saved) || saved.length !== sent.length) return;
    const idMap = {};
    sent.forEach((w, ix) => {
      const real = saved[ix]?._id ? String(saved[ix]._id) : "";
      if (real && !w._id && real !== w.i) idMap[w.i] = real;
    });
    if (!Object.keys(idMap).length) return;
    setWidgets((prev) => prev.map((w) => {
      let next = w;
      if (!w._id && idMap[w.i]) next = { ...next, _id: idMap[w.i] };
      const opts = w.options || {};
      const fixed = {};
      WIDGET_REF_KEYS.forEach((k) => {
        if (typeof opts[k] === "string" && idMap[opts[k]]) fixed[k] = idMap[opts[k]];
      });
      if (Object.keys(fixed).length) next = { ...next, options: { ...opts, ...fixed } };
      return next;
    }));
  };

  const persist = useCallback(async (list) => {
    try {
      const res = await UPDATE_DASHBOARD(dashboard._id, {
        name,
        visibility: {
          mode: visibility,
          /* Both audiences travel together — a board can be a whole team
             plus a few named people. Any non-audience mode clears both, so
             switching to Only me can't leave a list still granting access. */
          workspaceIds: AUDIENCE_MODES.includes(visibility) ? wsIds : [],
          userIds: AUDIENCE_MODES.includes(visibility) ? playerIds : [],
        },
        showOnTv,
        template: templateId,
        dateRange: dateRangePayload(),
        widgets: serialize(list),
      });
      setSaving(false);
      setSavedAt(Date.now());
      adoptServerIds(list || widgets, res?.data?.widgets);
      if (res?.data && onSaved) onSaved(res.data);
    } catch (err) {
      setSaving(false);
      flash("Couldn't save — will retry on next change.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard._id, name, visibility, wsIds, playerIds, showOnTv, templateId, period, customRange, serialize, onSaved]);

  const doSave = useCallback(() => persist(), [persist]);

  /* A quick-edit save path for the rare card that lets an admin change a
     value right on the card (Payroll vs Revenue's per-employee pay rates)
     instead of through the Setup drawer. Saves immediately rather than
     riding the 900ms debounce below — that debounce is cancelled outright if
     the tab closes or reloads before it fires (see the effect's cleanup), so
     a click-and-refresh a moment later silently lost the rate. Reads the
     `next` array straight out of the setWidgets updater (it runs
     synchronously) instead of `widgets` — the state update it just
     scheduled hasn't landed in this render's closure yet. Admin-only: a
     non-admin can view this dashboard but never edit it. */
  const patchWidgetOptions = (widgetId, patchFn) => {
    if (frozen || !dashboard._id) return;
    let nextWidgets = null;
    setWidgets((prev) => {
      const next = prev.map((w) => {
        if (w.i !== widgetId || typeof patchFn !== "function") return w;
        const patched = patchFn(w.options || {});
        return patched ? { ...w, options: patched } : w;
      });
      nextWidgets = next;
      return next;
    });
    if (nextWidgets) {
      setSaving(true);
      persist(nextWidgets);
    }
  };

  /* What else is on this board, for the one card that reads another: Revenue,
     Expenses & Labor takes its Labor figure from a Payroll vs Revenue card
     sitting beside it, so it has to be able to see (and name) its siblings.
     Memoised on `widgets` — ComponentCard spreads boardCtx into every card's
     props, and a fresh array identity on each parent render would re-render
     the whole board for nothing. */
  const boardWidgets = useMemo(
    () => widgets
      .filter((w) => w.kind === "component" && w.comp)
      .map((w) => ({
        id: w.i,
        _id: w._id,
        comp: w.comp,
        title: w.title || w.options?.title || "",
        options: w.options || {},
      })),
    [widgets],
  );

  const boardCtx = {
    boardWidgets,
    /* Which board a card is on. A manual gate is chased from the CARD — by the
       person whose money it is holding up — so the popover needs to be able to
       name the gate back to the server. */
    boardId: dashboard._id || null,
    boardWs: hasCombined ? boardWs : null,
    boardWindow,
    boardCustom,
    globalRange: boardCustom,
    inheritWorkspaceIds,
    saveWidgetSettings: isAdmin ? patchWidgetOptions : null,
  };

  // Debounced auto-save, but ONLY when content actually changes — parent
  // re-renders refresh doSave's identity and must not trigger phantom saves
  // (a phantom save after a remount can overwrite real widgets with stale ones).
  // Period (Today / This year) is view state — persist it quietly without the
  // "Saving…" banner or a full widget rewrite on every filter click.
  const doSaveRef = useRef(doSave);
  useEffect(() => {
    doSaveRef.current = doSave;
  }, [doSave]);
  useEffect(() => {
    if (frozen) return undefined;
    if (!loadedRef.current) {
      loadedRef.current = true;
      return undefined;
    }
    setSaving(true);
    const t = setTimeout(() => doSaveRef.current(), 900);
    return () => clearTimeout(t);
  }, [name, widgets, visibility, wsIds, playerIds, showOnTv, templateId, frozen]);

  useEffect(() => {
    if (frozen || !dashboard._id) return undefined;
    if (!loadedRef.current) return undefined;
    const t = setTimeout(() => {
      UPDATE_DASHBOARD(dashboard._id, { dateRange: dateRangePayload() }).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [period, customRange?.from, customRange?.to, dashboard._id, frozen]);

  // Unfilled slots of this dashboard's template — shown as ghost placeholders
  // (edit mode only). A slot counts as filled when any widget overlaps it.
  const ghosts = useMemo(() => {
    const tpl = TEMPLATES.find((t) => t.id === templateId);
    if (!tpl || !editing) return [];
    const overlaps = (s, w) =>
      s.x < (w.x || 0) + (w.w || 3) &&
      (w.x || 0) < s.x + s.w &&
      s.y < (w.y || 0) + (w.h || 3) &&
      (w.y || 0) < s.y + s.h;
    return tpl.slots
      .map((s, idx) => ({ ...s, gi: `ghost-${idx}` }))
      .filter((s) => !widgets.some((w) => overlaps(s, w)));
  }, [templateId, widgets, editing]);

  /* ── never let a card clip its own content ───────────────────────────────
     A card's height is a saved number, but its content is not: switch a payout
     curve's gating on and the card grows a line it had no room for, so the
     bottom of it disappeared under the card's own `overflow: hidden`.

     Cards that can do this expose a natural-height interior as
     `data-hug-content` (the same marker the workspace canvas measures). We read
     that interior and raise the row's FLOOR to fit it — a floor, not a height,
     so a deliberately taller card keeps its size and only a too-short one is
     corrected. Nothing here ever shrinks a card. */
  const [fitRows, setFitRows] = useState({});
  useEffect(() => {
    const root = gridWrapRef.current;
    if (!root || typeof ResizeObserver === "undefined") return undefined;
    const ROW = 40;
    const GAP = 16;
    const measure = () => {
      setFitRows((prev) => {
        let changed = false;
        const next = { ...prev };
        root.querySelectorAll("[data-w-id]").forEach((host) => {
          const inner = host.querySelector("[data-hug-content]");
          const id = host.getAttribute("data-w-id");
          if (!inner || !id) return;
          const px = inner.scrollHeight;
          if (!px) return;
          const rows = Math.max(1, Math.ceil((px + GAP) / (ROW + GAP)));
          if (next[id] !== rows) {
            next[id] = rows;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    };
    const ro = new ResizeObserver(measure);
    const watched = new Set();
    const attach = () => {
      root.querySelectorAll("[data-hug-content]").forEach((inner) => {
        if (watched.has(inner)) return;
        watched.add(inner);
        ro.observe(inner);
      });
    };
    attach();
    measure();
    /* Interiors arrive late — a card paints an empty state before its metrics
       land — so keep picking up nodes that were not there on the first pass. */
    const mo = new MutationObserver(() => { attach(); measure(); });
    mo.observe(root, { childList: true, subtree: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
      watched.clear();
    };
    /* Re-arm when the grid itself appears: on the first commit the board is
       still loading and gridWrapRef is null, so a mount-only effect would
       observe nothing and never retry. */
  }, [widgets.length, isDesktop]);

  const layout = useMemo(
    () => [
      ...widgets.map((w) => {
        const def = w.kind === "component" ? widgetById(w.comp) : null;
        const defFloor = def ? minSizeFor(def).h : 3;
        /* The taller of the component's declared minimum and what its content
           actually measures right now. */
        const floor = Math.max(defFloor, fitRows[w.i] || 0);
        return {
          i: w.i,
          x: w.x || 0,
          y: w.y || 0,
          w: w.w || 3,
          h: Math.max(w.h || 3, floor),
          minW: 2,
          minH: floor,
        };
      }),
      ...ghosts.map((g) => ({ i: g.gi, x: g.x, y: g.y, w: g.w, h: g.h, static: true })),
    ],
    [widgets, ghosts, fitRows]
  );

  const onLayoutChange = (next) => {
    // A swap was just applied in onDragStop — ignore the grid's own result.
    if (skipNextLayout.current) {
      skipNextLayout.current = false;
      return;
    }
    setWidgets((prev) =>
      prev.map((w) => {
        const l = next.find((n) => n.i === w.i);
        return l ? { ...w, x: l.x, y: l.y, w: l.w, h: l.h } : w;
      })
    );
  };

  // Grid coordinates (fractional col/row) of a pointer event.
  const gridPoint = (e) => {
    if (!e) return null;
    const gridEl = gridWrapRef.current?.querySelector(".react-grid-layout");
    if (!gridEl) return null;
    const rect = gridEl.getBoundingClientRect();
    const point = e.changedTouches?.[0] || e.touches?.[0] || e;
    if (point.clientX == null) return null;
    const colW = (rect.width - 16 * 11) / 12;
    return {
      col: (point.clientX - rect.left) / (colW + 16),
      row: (point.clientY - rect.top) / (40 + 16),
    };
  };

  // What the drag is hovering: another card (swap) or a ghost slot (move into).
  const targetAtPointer = (e) => {
    const info = dragInfo.current;
    const pt = gridPoint(e);
    if (!info || !pt) return null;
    const hit = (r) =>
      pt.col >= (r.x || 0) && pt.col < (r.x || 0) + (r.w || 3) &&
      pt.row >= (r.y || 0) && pt.row < (r.y || 0) + (r.h || 3);
    const widget = info.snapshot.find((w) => w.i !== info.id && hit(w));
    if (widget) return { kind: "widget", item: widget };
    const ghost = ghosts.find((g) => hit(g));
    if (ghost) return { kind: "ghost", item: ghost };
    return null;
  };

  const onDragStart = (l, oldItem) => {
    skipNextLayout.current = false;
    dragInfo.current = { id: oldItem.i, snapshot: widgets.map((w) => ({ ...w })) };
  };

  // Light up the card or ghost the drag is hovering so the drop reads first.
  const onDrag = (l, oldItem, newItem, placeholder, e) => {
    const t = targetAtPointer(e);
    const id = t ? (t.item.i || t.item.gi) : null;
    if (id !== swapTargetRef.current) {
      swapTargetRef.current = id;
      setSwapTarget(id);
    }
  };

  // Released over another card: swap the two — each metric takes the other's
  // slot (position AND size). Released over a ghost slot: move into it. The
  // rest of the template never moves.
  const onDragStop = (l, oldItem, newItem, placeholder, e) => {
    const info = dragInfo.current;
    const target = targetAtPointer(e);
    dragInfo.current = null;
    swapTargetRef.current = null;
    setSwapTarget(null);
    if (!info || !target) return;
    const src = info.snapshot.find((w) => w.i === info.id);
    if (!src) return;
    const t = target.item;
    skipNextLayout.current = true;
    if (target.kind === "ghost") {
      setWidgets(
        info.snapshot.map((w) =>
          w.i === src.i ? { ...w, x: t.x, y: t.y, w: t.w, h: t.h } : w
        )
      );
      flash("Moved into the open spot");
      return;
    }
    setWidgets(
      info.snapshot.map((w) => {
        if (w.i === src.i) return { ...w, x: t.x, y: t.y, w: t.w, h: t.h };
        if (w.i === t.i) return { ...w, x: src.x, y: src.y, w: src.w, h: src.h };
        return w;
      })
    );
    flash("Swapped");
  };

  const bottomY = () => widgets.reduce((max, w) => Math.max(max, (w.y || 0) + (w.h || 3)), 0);

  // Add the picked metrics — into the clicked ghost slot when one is pending
  // (the slot's shape decides the chart type), otherwise appended below.
  const addMetrics = (metricIds) => {
    const fillSlot = pendingSlot;
    setWidgets((prev) => {
      const startY = prev.reduce((max, w) => Math.max(max, (w.y || 0) + (w.h || 3)), 0);
      const next = [...prev];
      let rest = metricIds;
      if (fillSlot) {
        next.push({
          i: nid(),
          metric: metricIds[0],
          title: "",
          chartType: fillSlot.t,
          x: fillSlot.x,
          y: fillSlot.y,
          w: fillSlot.w,
          h: fillSlot.h,
          options: { accent: ACCENT_CYCLE[prev.length % ACCENT_CYCLE.length] },
        });
        rest = metricIds.slice(1);
      }
      rest.forEach((id, i) => {
        const type = defaultTypeForMetric();
        const size = defaultSize(type);
        next.push({ i: nid(), metric: id, title: "", chartType: type, x: 0, y: startY + i * size.h, ...size, options: { accent: ACCENT_CYCLE[(next.length) % ACCENT_CYCLE.length] } });
      });
      return next;
    });
    setPendingSlot(null);
    setAddOpen(false);
    if (!editing) setEditing(true);
    flash(metricIds.length === 1 ? "Metric added" : `${metricIds.length} metrics added`);
  };

  // Add a game component — lands below everything, pointed at the currently
  // selected workspace until its ••• menu says otherwise.
  const addComponent = (c) => {
    setWidgets((prev) => {
      const startY = prev.reduce((max, w) => Math.max(max, (w.y || 0) + (w.h || 3)), 0);
      const def = widgetById(c.comp);
      const size = c.size || (def ? sizeFor(def) : { w: 6, h: 8 });
      const opts = {
        workspaceId: selectedWorkspace?._id || null,
        ...(c.options || {}),
      };
      if (def?.annualSetup && !opts.window) opts.window = "ytd";
      /* An example ladder, so the card shows a real bonus the moment it lands. */
      if (def?.netBonusSetup && !opts.netBonus) opts.netBonus = defaultNetBonus();
      return [
        ...prev,
        fitComponentHeight({
          i: nid(),
          kind: "component",
          comp: c.comp,
          title: c.title || c.label || "",
          x: 0,
          y: startY,
          w: size.w,
          h: size.h,
          options: opts,
        }),
      ];
    });
    if (!editing) setEditing(true);
    flash(`${c.label || c.title || "Component"} added`);
  };

  // The drawer editor writes title + options together, live.
  // Never let a patch drop kind/comp — that autosaved Team Monitoring as
  // empty metric cards ("Metric unavailable") while options survived.
  const changeComponent = (i, patch) =>
    setWidgets((prev) => prev.map((w) => {
      if (w.i !== i) return w;
      const next = { ...w, ...patch };
      if (w.kind === "component" || w.comp) {
        next.kind = "component";
        next.comp = (patch && patch.comp) || w.comp;
      }
      return next;
    }));

  // Present mode — dashboard only, full screen, chrome hidden.
  const openPresent = () => {
    setPresenting(true);
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
  };
  const closePresent = useCallback(() => {
    setPresenting(false);
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  }, []);
  useEffect(() => {
    if (!presenting) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closePresent();
    };
    // Leaving browser fullscreen (Esc) also leaves present mode.
    const onFsChange = () => {
      if (!document.fullscreenElement) setPresenting(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFsChange);
    };
  }, [presenting, closePresent]);

  const changeType = (i, t) =>
    setWidgets((prev) => prev.map((w) => (w.i === i ? { ...w, chartType: t } : w)));

  const duplicateWidget = (i) =>
    setWidgets((prev) => {
      const src = prev.find((w) => w.i === i);
      if (!src) return prev;
      return [...prev, { ...src, _id: undefined, i: nid(), y: bottomY() }];
    });

  // Copy one widget/component onto a DIFFERENT dashboard — fetches that
  // board fresh (so a concurrently-edited target never gets clobbered),
  // appends the copy below its existing widgets, and saves just the
  // `widgets` field so the rest of that dashboard's settings are untouched.
  const [copyWidgetId, setCopyWidgetId] = useState(null);
  const copySrc = widgets.find((w) => w.i === copyWidgetId) || null;
  const copySrcLabel = copySrc
    ? copySrc.title || (copySrc.kind === "component" ? widgetById(copySrc.comp)?.title : "") || ""
    : "";

  const copyWidgetToDashboard = async (widgetId, targetId) => {
    const src = widgets.find((w) => w.i === widgetId);
    if (!src) throw new Error("That widget no longer exists on this dashboard.");
    const shaped = serialize([src])[0];
    delete shaped._id;
    const { data: target } = await GET_DASHBOARD(targetId);
    const existingWidgets = Array.isArray(target?.widgets) ? target.widgets : [];
    const bottomTargetY = existingWidgets.reduce(
      (max, w) => Math.max(max, (Number(w.y) || 0) + (Number(w.h) || 3)),
      0
    );
    const res = await UPDATE_DASHBOARD(targetId, {
      widgets: [...existingWidgets, { ...shaped, x: 0, y: bottomTargetY }],
    });
    if (res?.data && onSaved) onSaved(res.data);
    return res?.data || null;
  };

  const removeWidget = (i) => setWidgets((prev) => prev.filter((w) => w.i !== i));

  const saveEdit = (patch) =>
    setWidgets((prev) => prev.map((w) => (w.i === editId ? { ...w, ...patch } : w)));

  const editWidget = widgets.find((w) => w.i === editId) || null;

  let savedLabel = "Saved";
  if (saving) savedLabel = "Saving…";
  else if (savedAt) savedLabel = "All changes saved";

  /* Older boards were saved as 'workspaces' or 'players'; both are the same
     audience mode now, so the menu shows one option for all three. */
  const isAudience = AUDIENCE_MODES.includes(visibility);

  /* The mode chip counts the audience; the chip beside it names it. Two
     different readings of the same thing, so neither has to be expanded. */
  const audienceSummary = () => {
    const bits = [];
    if (wsIds.length) bits.push(`${wsIds.length} workspace${wsIds.length === 1 ? "" : "s"}`);
    if (playerIds.length) bits.push(`${playerIds.length} player${playerIds.length === 1 ? "" : "s"}`);
    return bits.length ? bits.join(" · ") : "Pick who can see";
  };

  // Visibility trigger — precomputed so the menu button stays a stable element.
  let visibilityIcon = <IcGlobe size={14} />;
  let visibilityLabel = "Public";
  if (visibility === "private") {
    visibilityIcon = <IcLock size={14} />;
    visibilityLabel = "Private";
  } else if (isAudience) {
    visibilityIcon = <IcUsers size={14} />;
    visibilityLabel = audienceSummary();
  } else if (visibility === "admins") {
    visibilityIcon = <IcShield size={14} />;
    visibilityLabel = "Admins";
  }

  /* Everyone in the company, for the "Specific players" picker. The
     dictionary is already in redux (loaded at login), so the picker costs no
     extra fetch. The owner is left out — they can always open their own
     board, so offering to invite them would be a no-op checkbox. */
  const companyPlayers = useMemo(() => {
    const rows = Object.entries(usersDict || {})
      /* Archived accounts can't sign in, so listing them would only pad the
         roster. Someone invited but not yet logged in IS listed — sharing
         ahead of their first day is the point. */
      .filter(([, u]) => !u?.isArchived)
      .map(([id, u]) => ({
        id: String(id),
        name: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || u?.email || "Player",
        workspace: wsNameById[String(u?.groupId)] || "",
      }));
    return rows
      .filter((p) => p.id !== String(dashboard.ownerId))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [usersDict, wsNameById, dashboard.ownerId]);

  /* Defined after companyPlayers, which it reads. */
  const audienceNames = () => {
    const names = [
      ...(workspaces || []).filter((w) => wsIds.includes(String(w._id))).map((w) => w.name),
      ...companyPlayers.filter((p) => playerIds.includes(p.id)).map((p) => p.name),
    ];
    if (!names.length) return "Pick who can see";
    return names.slice(0, 2).join(", ") + (names.length > 2 ? ` +${names.length - 2}` : "");
  };

  /* One search box over both lists — you type a name or a team and see
     whichever matches, rather than guessing which section to look in. */
  const audienceQ = audienceQuery.trim().toLowerCase();
  const playersShown = useMemo(
    () => (audienceQ ? companyPlayers.filter((p) => p.name.toLowerCase().includes(audienceQ)) : companyPlayers),
    [companyPlayers, audienceQ],
  );
  const workspacesShown = useMemo(
    () => (audienceQ
      ? (workspaces || []).filter((w) => (w.name || "").toLowerCase().includes(audienceQ))
      : (workspaces || [])),
    [workspaces, audienceQ],
  );

  const metricCatalog = useMemo(
    () => {
      const wsName = {};
      (workspaces || []).forEach((w) => { wsName[String(w._id)] = w.name; });
      const real = (metrics || [])
        .filter((k) => k && (k.title || k.name || k.label))
        .map((k) => {
          const wids = Array.isArray(k.workspaceIds) ? k.workspaceIds.map(String) : [];
          /* Prefer the workspace name when this metric lives on exactly one
             team — that's what the picker header is meant to show. Multi-ws
             shared titles fall back to the integration / Manual entry label. */
          let group = "Manual entry";
          if (wids.length === 1 && wsName[wids[0]]) group = wsName[wids[0]];
          else {
            const raw = k.sourceName || k.source;
            if (typeof raw === "string" && raw.trim() && raw !== "[object Object]") {
              group = raw.trim();
            } else if (raw && typeof raw === "object") {
              const n = raw.name || raw.label || raw.title || raw.code;
              if (typeof n === "string" && n.trim()) group = n.trim();
            }
          }
          return {
            id: k.id || k._id || (k.title || k.name || k.label || "").trim(),
            label: (k.title || k.name || k.label || "").trim(),
            kind: k.kind === "$" || k.kind === "%" ? k.kind : "#",
            target: Number(k.target) || 0,
            value: Number(k.value ?? k.amount) || 0,
            group,
            workspaceIds: wids,
            ids: Array.isArray(k.ids) ? k.ids.map(String) : [],
          };
        });
      return [
        ...real,
        {
          id: AVG_DAILY_REVENUE_ID, label: "Average Daily Revenue", kind: "$",
          target: 0, value: 0, group: "Derived", workspaceIds: [], ids: [], derived: true,
        },
        {
          id: TECH_REVENUE_PER_HOUR_ID, label: "Technicians' Revenue per Hour", kind: "$",
          target: 0, value: 0, group: "Derived", workspaceIds: [], ids: [], derived: true,
        },
        {
          id: BEAT_YETI_RATE_ID, label: "Beat the Yeti rate", kind: "%",
          target: 0, value: 0, group: "Derived", workspaceIds: [], ids: [], derived: true,
        },
      ];
    },
    [metrics, workspaces],
  );


  return (
    <div className={`db-root${drawerOpen ? " panel-open" : ""}`}>
      <div className={`db-abovebar${barStuck ? " is-stuck" : ""}`} ref={aboveBarRef}>
        <button type="button" className="db-back" onClick={onBack}>
          <IcBack size={14} />
          {backLabel || "All dashboards"}
        </button>

        <div className="db-actions">
          {!readOnly && (
          <span className="db-desktop-only">
            <MiniMenu
              icon={visibilityIcon}
              label={visibilityLabel}
              items={[
                { id: "private", label: "Only me", icon: <IcLock size={14} />, on: visibility === "private" },
                { id: "admins", label: "Company admins", icon: <IcShield size={14} />, on: visibility === "admins" },
                { id: "shared", label: "Workspaces & players", icon: <IcUsers size={14} />, on: isAudience },
                { id: "public", label: "Whole company", icon: <IcGlobe size={14} />, on: visibility === "public" },
              ]}
              onPick={(id) => {
                if (id !== "private" && !isAdmin) {
                  flash("Only admins can share a dashboard beyond themselves.");
                  return;
                }
                setVisibility(id);
                if (id === "private") setShowOnTv(false);
                if (id === "shared") {
                  /* Seed the current workspace only on a board that has
                     picked nobody at all — otherwise reopening the menu
                     would quietly re-add a team you had just unticked. */
                  if (!wsIds.length && !playerIds.length && selectedWorkspace?._id) {
                    setWsIds([String(selectedWorkspace._id)]);
                  }
                  setAudienceQuery("");
                  setAudienceOpen(true);
                }
              }}
            />
          </span>
          )}
          {!readOnly && isAudience && (
            <span className="db-desktop-only db-mini" style={{ position: "relative" }} ref={audienceRef}>
              <button type="button" className="db-btn sm" onClick={() => setAudienceOpen((v) => !v)}>
                {audienceNames()}
              </button>
              {audienceOpen && (
                <span className="db-mini-menu db-audience" role="menu">
                  <input
                    className="db-audience-search"
                    type="text"
                    value={audienceQuery}
                    placeholder="Search workspaces and players"
                    onChange={(e) => setAudienceQuery(e.target.value)}
                  />
                  <span className="db-audience-list">
                    {!!workspacesShown.length && (
                      <span className="db-audience-head">Workspaces</span>
                    )}
                    {workspacesShown.map((w) => {
                      const on = wsIds.includes(String(w._id));
                      return (
                        <button
                          key={w._id}
                          type="button"
                          role="menuitemcheckbox"
                          aria-checked={on}
                          onClick={() => {
                            setWsIds((prev) =>
                              on ? prev.filter((id) => id !== String(w._id)) : [...prev, String(w._id)]
                            );
                          }}
                        >
                          <span className="db-audience-tick" aria-hidden="true">
                            {on && <IcCheck size={11} />}
                          </span>
                          <span className="db-audience-name">
                            <b style={{ paddingLeft: w.parentId ? 10 : 0 }}>{w.name}</b>
                            <em>Everyone on this team</em>
                          </span>
                        </button>
                      );
                    })}

                    {!!playersShown.length && (
                      <span className="db-audience-head">Players</span>
                    )}
                    {playersShown.map((p) => {
                      const on = playerIds.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          role="menuitemcheckbox"
                          aria-checked={on}
                          onClick={() => {
                            setPlayerIds((prev) =>
                              on ? prev.filter((id) => id !== p.id) : [...prev, p.id]
                            );
                          }}
                        >
                          <span className="db-audience-tick" aria-hidden="true">
                            {on && <IcCheck size={11} />}
                          </span>
                          <span className="db-audience-name">
                            <b>{p.name}</b>
                            {p.workspace && <em>{p.workspace}</em>}
                          </span>
                        </button>
                      );
                    })}

                    {!workspacesShown.length && !playersShown.length && (
                      <span className="db-audience-empty">Nothing matches that.</span>
                    )}
                  </span>
                </span>
              )}
            </span>
          )}
          {isAdmin && !readOnly && (
            <button
              type="button"
              className={`db-btn sm db-desktop-only db-tvbtn ${showOnTv ? "on" : ""}`}
              onClick={() => {
                if (!showOnTv) { setTvConfirm(true); return; }
                setShowOnTv(false);
                flash("No longer available as a TV view");
              }}
            >
              <IcTv size={14} />
              {showOnTv ? "On TV" : "Show on TV"}
            </button>
          )}
          {widgets.length > 0 && (
            <button type="button" className="db-btn sm" onClick={openPresent}>
              <IcExpand size={14} />
              Present
            </button>
          )}
          {!readOnly && (
          <button
            type="button"
            className={`db-btn sm db-desktop-only ${editing ? "primary" : ""}`}
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? "Done" : "Edit"}
          </button>
          )}
          {!readOnly && (
          <span className="db-desktop-only db-addmenu">
            <MiniMenu
              icon={<IcPlus size={14} />}
              label="Add"
              items={[
                { id: "component", label: "Add component", icon: <IcBlank size={14} /> },
                { id: "metric", label: "Add metric", icon: <IcStat size={14} /> },
              ]}
              onPick={(id) => {
                if (id === "component") setDrawerOpen(true);
                else setAddOpen(true);
              }}
            />
          </span>
          )}
        </div>
      </div>

      <div className="db-frame">
      <div className="db-topbar">
        <div style={{ minWidth: 0 }}>
          <input
            className="db-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            readOnly={readOnly}
            aria-label="Dashboard name"
          />
          {readOnly ? (
            <div className="db-sub">Shared with you</div>
          ) : (
            <div className="db-sub">
              <span className={`db-savedot ${saving ? "saving" : ""}`} />
              {savedLabel}
            </div>
          )}
        </div>

        {/* Viewing filters belong to the DASHBOARD, not the manage bar — they
            change what it shows and travel with it (viewers, TV). Period is
            always here; the workspace filter only when a card pulls 2+ workspaces. */}
        <div className="db-viewing-row db-desktop-only">
          <span className="db-viewing-label">Viewing</span>
          <span style={{ position: "relative" }}>
            <MiniMenu
              label={periodTitle}
              items={PERIODS.map((p) => ({ id: p.id, label: p.label, on: period === p.id }))}
              onPick={(id) => {
                if (id === "custom") {
                  setRangeOpen(true);
                  return;
                }
                /* Drop the previous custom from/to so the label and fetches
                   cannot resurrect an old range after switching presets. */
                setRangeOpen(false);
                setCustomRange({ from: "", to: "" });
                setPeriod(id);
              }}
            />
            {rangeOpen && (
              <RangeCalendar
                value={customRange || { from: "", to: "" }}
                onApply={(r) => {
                  setCustomRange({
                    from: (r && r.from) || "",
                    to: (r && r.to) || "",
                  });
                  setPeriod("custom");
                  setRangeOpen(false);
                }}
                onClose={() => setRangeOpen(false)}
              />
            )}
          </span>
          {hasCombined && (
            <span className="db-viewing">
              <LightSelect
                options={wsOptions}
                value={boardWs}
                width={boardWs === "all" ? 150 : 168}
                height={30}
                fontSize={12}
                radius={8}
                onChange={(o) => setBoardWs(o.value)}
              />
            </span>
          )}
        </div>
      </div>

      <div className="db-frame-body">
      <div className="db-mobile-note">
        <IcAlert size={16} />
        <span>You&apos;re viewing this dashboard in read-only mode on mobile. Switch to a desktop browser to add, edit, arrange, or remove widgets.</span>
      </div>
      {editing && widgets.length > 0 && (
        <div className="db-hint">
          <IcMove size={14} />
          Drop a card onto another to swap places · pull the bottom-right corner to resize · use ••• to change how a metric looks.
        </div>
      )}

      {(() => {
        if (widgets.length === 0 && ghosts.length === 0) {
          return (
            <div className="db-tpl-pick">
              <h3 className="db-tpl-h">Nothing here yet</h3>
              <p className="db-tpl-s">Add game components or metric charts — you can rearrange and restyle them any time.</p>
              <button type="button" className="db-btn primary db-desktop-only" onClick={() => setDrawerOpen(true)} style={{ margin: "0 auto" }}>
                <IcPlus />
                Add
              </button>
            </div>
          );
        }
        if (!isDesktop) {
          return <MobileWidgetStack widgets={widgets} metricsApi={metricsApi} boardCtx={boardCtx} />;
        }
        return (
          <div ref={gridWrapRef}>
          <Grid
            className={`db-canvas ${editing ? "editing" : ""}`}
            layout={layout}
            cols={12}
            rowHeight={40}
            margin={[16, 16]}
            containerPadding={[0, 0]}
            isDraggable={editing}
            isResizable={editing}
            draggableHandle=".wc-drag"
            compactType={null}
            preventCollision
            onLayoutChange={onLayoutChange}
            onDragStart={onDragStart}
            onDrag={onDrag}
            onDragStop={onDragStop}
            useCSSTransforms
          >
            {widgets.map((w) => {
              const wd = w.kind === "component" ? null : resolveWidgetData(metricsApi, w);
              return (
                <div key={w.i} data-w-id={w.i} className={swapTarget === w.i ? "db-swaptarget" : undefined}>
                  {w.kind === "component" ? (
                    <ComponentCard
                      widget={w}
                      period={period}
                      editing={editing}
                      onEdit={() => {
                        setCompEditId(w.i);
                        setDrawerOpen(true);
                      }}
                      onDuplicate={() => duplicateWidget(w.i)}
                      onCopyToDashboard={frozen ? undefined : () => setCopyWidgetId(w.i)}
                      onRemove={() => removeWidget(w.i)}
                      {...boardCtx}
            />
                  ) : (
                    <WidgetCard
                      widget={w}
                      metric={wd.metric}
                      series={wd.series}
                      summary={wd.summary}
                      loading={loading}
                      api={metricsApi}
                      editing={editing}
                      onChangeType={(t) => changeType(w.i, t)}
                      onEdit={() => setEditId(w.i)}
                      onDuplicate={() => duplicateWidget(w.i)}
                      onCopyToDashboard={frozen ? undefined : () => setCopyWidgetId(w.i)}
                      onRemove={() => removeWidget(w.i)}
                    />
                  )}
                </div>
              );
            })}
            {ghosts.map((g) => (
              <div key={g.gi} className={swapTarget === g.gi ? "db-swaptarget" : undefined}>
                <button
                  type="button"
                  className="db-ghost db-desktop-only"
                  onClick={() => {
                    setPendingSlot(g);
                    setAddOpen(true);
                  }}
                >
                  <IcPlus />
                  Add metric
                </button>
              </div>
            ))}
          </Grid>
          </div>
        );
      })()}

      </div>
      </div>

      <ComponentsDrawer
        metricCatalog={metricCatalog}
        dashboardId={dashboard._id || null}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setCompEditId(null);
        }}
        onAdd={addComponent}
        onPickMetrics={() => {
          setDrawerOpen(false);
          setAddOpen(true);
        }}
        boardWidgets={boardWidgets}
        editWidget={widgets.find((w) => w.i === compEditId) || null}
        onEditChange={(patch) => changeComponent(compEditId, patch)}
        onEditBack={() => setCompEditId(null)}
      />
      <AddMetricModal
        open={addOpen}
        metrics={metrics}
        getSummary={getSummary}
        getSeries={getSeries}
        template={null}
        onAdd={addMetrics}
        onClose={() => {
          setAddOpen(false);
          setPendingSlot(null);
        }}
      />
      <WidgetEditDrawer
        open={!!editId}
        widget={editWidget}
        metrics={metrics}
        api={metricsApi}
        onSave={saveEdit}
        onClose={() => setEditId(null)}
      />

      <CopyToDashboardModal
        open={!!copyWidgetId}
        widgetLabel={copySrcLabel}
        excludeId={dashboard._id}
        onClose={() => setCopyWidgetId(null)}
        onCopy={async (target) => {
          await copyWidgetToDashboard(copyWidgetId, target._id);
          flash(`Copied to "${target.name || "Untitled dashboard"}"`);
        }}
      />

      {tvConfirm && (
        <div
          className="db-modal-scrim"
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setTvConfirm(false); }}
        >
          <div className="db-modal" role="dialog" aria-modal="true" aria-label="Show on TV">
            <div className="db-modal-inner">
              <div className="db-modal-head">
                <div>
                  <div className="db-modal-t">Show {name ? `“${name}”` : "this dashboard"} on TV?</div>
                  <div className="db-modal-s">Here’s what turning this on does.</div>
                </div>
                <button type="button" className="db-iconbtn" onClick={() => setTvConfirm(false)} aria-label="Close">
                  <IcClose />
                </button>
              </div>
              <ul className="db-tvconfirm">
                <li><IcTv size={16} /><span>It becomes a <b>TV view</b> for this workspace — anyone casting this workspace to a TV can pick it to play.</span></li>
                <li><IcUsers size={16} /><span>You still choose <b>which TVs</b> show it in Settings → TV View. Turning this on only makes it available.</span></li>
                <li><IcCheck size={16} /><span>It plays full-screen and <b>updates live</b> as your numbers change — nothing to reopen.</span></li>
              </ul>
              <div className="db-modal-foot">
                <button type="button" className="db-btn ghost" onClick={() => setTvConfirm(false)}>Cancel</button>
                <button
                  type="button"
                  className="db-btn primary"
                  onClick={() => {
                    setShowOnTv(true);
                    setTvConfirm(false);
                    flash("Available as a TV view — choose its TVs in Settings → TV View");
                  }}
                >
                  <IcTv size={14} /> Show on TV
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {presenting && (
        <div className="db-present" role="dialog" aria-modal="true" aria-label="Present dashboard">
          <div className="db-present-head">
            <div>
              <div className="db-present-t">{name}</div>
              <div className="db-present-s">{periodTitle}</div>
            </div>
            <button type="button" className="db-btn db-present-exit" onClick={closePresent}>
              <IcClose size={14} />
              Exit
            </button>
          </div>
          <div className="db-present-body">
            {isDesktop ? (
              <Grid
                className="db-canvas"
                layout={widgets.map((w) => ({ i: w.i, x: w.x || 0, y: w.y || 0, w: w.w || 3, h: w.h || 3, static: true }))}
                cols={12}
                rowHeight={40}
                margin={[16, 16]}
                containerPadding={[0, 0]}
                isDraggable={false}
                isResizable={false}
                useCSSTransforms
              >
                {widgets.map((w) => {
                  const wd = w.kind === "component" ? null : resolveWidgetData(metricsApi, w);
                  return (
                    <div key={w.i}>
                      {w.kind === "component" ? (
                        <ComponentCard
                          widget={w}
                          period={period}
                          editing={false}
                          onDuplicate={() => {}}
                          onRemove={() => {}}
                          {...boardCtx}
            />
                      ) : (
                        <WidgetCard
                          widget={w}
                          metric={wd.metric}
                          series={wd.series}
                          summary={wd.summary}
                          loading={loading}
                          api={metricsApi}
                          editing={false}
                          onChangeType={() => {}}
                          onEdit={() => {}}
                          onDuplicate={() => {}}
                          onRemove={() => {}}
                        />
                      )}
                    </div>
                  );
                })}
              </Grid>
            ) : (
              <MobileWidgetStack widgets={widgets} metricsApi={metricsApi} boardCtx={boardCtx} />
            )}
          </div>
        </div>
      )}

      <div className={`db-toast ${toast ? "on" : ""}`}>{toast}</div>
    </div>
  );
}

DashboardBuilder.propTypes = {
  dashboard: PropTypes.object.isRequired,
  metricsApi: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  demo: PropTypes.bool,
  readOnly: PropTypes.bool,
  backLabel: PropTypes.string,
};
